// src/pages/AdsRegister/AdsRegistStep3.jsx (경로는 프로젝트 구조에 맞게)
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import SectionTitle from "../../../assets/components/TitleGroup";
import { SingleButton } from "../../../assets/components/ButtonGroup";

// 🔐 KCB 관련 상수
const KCB_COMMONSVL_URL = "https://safe.ok-name.co.kr/CommonSvl";
const KCB_TC = "kcb.oknm.online.main.popup.cmd.P941_CertChoiceCmd";

const START_API = `${process.env.REACT_APP_FASTAPI_SEVER_URL}/test/kcb/start`;
const DECRYPT_API = `${process.env.REACT_APP_FASTAPI_SEVER_URL}/test/kcb/decrypt`;

const AdsRegistStep3 = ({ onNext }) => {
    const [loading, setLoading] = useState(false);
    const [phase, setPhase] = useState("init"); // init → starting → jumping → returning → decrypting → done
    const [error, setError] = useState("");

    // 현재 URL 기준으로, 항상 step=3으로 돌아오게 return_url 구성
    const returnUrl = useMemo(() => {
        const u = new URL(window.location.href);
        u.searchParams.set("step", "3");
        u.hash = "";
        return u.toString();
    }, []);

    // KCB 본인인증 시작 (버튼 클릭 시 호출)
    const startKcbFlow = async () => {
        try {
            // 중복 실행 방지
            if (sessionStorage.getItem("kcb_starting") === "1") return;

            sessionStorage.setItem("kcb_starting", "1");
            setPhase("starting");
            setLoading(true);
            setError("");

            // 1) START 호출 (서비스 토큰 발급)
            const { data } = await axios.post(
                START_API,
                { return_url: returnUrl },
                { headers: { "Content-Type": "application/json" }, withCredentials: true }
            );

            // 복호화를 위해 서버가 준 enc 정보 저장
            if (data?.enc_key) {
                sessionStorage.setItem(
                    "kcb_enc",
                    JSON.stringify({
                        enc_key: data.enc_key,
                        enc_algo_cd: data.enc_algo_cd, // 1331
                        enc_iv: data.enc_iv,
                    })
                );
            }

            // 2) KCB 표준창으로 이동
            if (!data?.svc_tkn || !data?.idcf_mbr_com_cd) {
                throw new Error("인증 토큰이 유효하지 않습니다.");
            }

            setPhase("jumping");

            const form = document.createElement("form");
            form.method = "POST";
            form.action = KCB_COMMONSVL_URL;
            form.style.display = "none";
            form.target = "_self"; // WebView 대응

            [
                { name: "tc", value: KCB_TC },
                { name: "idcf_mbr_com_cd", value: String(data.idcf_mbr_com_cd) },
                { name: "svc_tkn", value: String(data.svc_tkn) },
            ].forEach(({ name, value }) => {
                const input = document.createElement("input");
                input.type = "hidden";
                input.name = name;
                input.value = value;
                form.appendChild(input);
            });

            document.body.appendChild(form);
            form.submit();
            // submit 후에는 보통 아래 코드 실행 안됨
            document.body.removeChild(form);
        } catch (e) {
            sessionStorage.removeItem("kcb_starting");
            const msg =
                e?.response?.data?.detail ||
                e?.message ||
                "본인인증 시작에 실패했습니다.";
            setError(msg);
            setLoading(false);
            setPhase("init");
        }
    };

    // KCB에서 돌아왔을 때(svc_tkn 존재) 복호화 → 성공 시 onNext() 호출
    useEffect(() => {
        const q = new URLSearchParams(window.location.search);
        const svcTkn = q.get("svc_tkn") || q.get("SVC_TKN");

        // 이미 처리된 상태면 무시
        if (sessionStorage.getItem("kcb_done") === "1") {
            setLoading(false);
            return;
        }

        // 돌아온 게 아니면 아무 것도 안 함 (버튼만 보이게)
        if (!svcTkn) {
            setLoading(false);
            setPhase("init");
            return;
        }

        setPhase("returning");

        const encJson = sessionStorage.getItem("kcb_enc");
        if (!encJson) {
            setError("인증 키 정보가 없어 복호화를 진행할 수 없습니다. 다시 시도해 주세요.");
            setLoading(false);
            return;
        }
        const enc = JSON.parse(encJson);

        (async () => {
            try {
                setPhase("decrypting");
                setLoading(true);
                setError("");

                const { data } = await axios.post(
                    DECRYPT_API,
                    {
                        svc_tkn: svcTkn,
                        enc_key: enc.enc_key,
                        enc_algo_cd: enc.enc_algo_cd,
                        enc_iv: enc.enc_iv,
                        user_id: Number(localStorage.getItem("user_id"))
                    },
                    { headers: { "Content-Type": "application/json" }, withCredentials: true }
                );

                // rsp_cd 체크
                if (data.rsp_cd !== "B000") {
                    setPhase("init");
                    setError(data.rsp_msg || "본인인증에 실패했습니다. 다시 시도해 주세요.");
                    setLoading(false);
                    return;
                }

                // 진짜 성공
                setPhase("done");
                sessionStorage.setItem("kcb_done", "1");

                // URL에서 svc_tkn 제거 (새로고침 시 다시 복호화 방지)
                const nextUrl = new URL(window.location.href);
                nextUrl.searchParams.delete("svc_tkn");
                nextUrl.searchParams.delete("SVC_TKN");
                window.history.replaceState(null, "", nextUrl.toString());

                // 4단계로 진행 (부모 스텝 이동)
                if (onNext) onNext();
            } catch (e) {
                const detail = e?.response?.data?.detail;
                const msg =
                    (typeof detail === "string" ? detail : detail?.rsp_msg) ||
                    e?.message ||
                    "복호화 중 오류가 발생했습니다.";

                setError(msg);
                setLoading(false);
                setPhase("init");
            } finally {
                // 사용한 키 제거
                sessionStorage.removeItem("kcb_enc");
            }
        })();
    }, [onNext]);

    const retry = () => {
        sessionStorage.removeItem("kcb_starting");
        sessionStorage.removeItem("kcb_done");
        sessionStorage.removeItem("kcb_enc");
        setError("");
        setPhase("init");
        setLoading(false);
    };

    return (
        <div className="flex flex-col h-dvh">
            <div className="p-[16px]">
                <SectionTitle
                    title={"위즈마켓 회원가입하기"}
                    subTitle={
                        "wizMarket을 이용하기 위해서는 본인인증이 필요해요."
                    }
                    className={"pb-[40px]"}
                />

                {loading && (
                    <p className="mt-4 text-sm text-gray-500">
                        상태:{" "}
                        {phase === "starting" && "인증 토큰 발급 중"}
                        {phase === "jumping" && "인증 화면으로 이동 중"}
                        {phase === "returning" && "인증 결과 수신 중"}
                        {phase === "decrypting" && "결과 복호화 중"}
                        {phase === "done" && "완료"}
                    </p>
                )}

                {error && (
                    <div className="mt-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                        {error}
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={retry}
                                className="px-3 py-2 rounded-md border text-sm"
                            >
                                다시 시도
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-surface-default">
                <SingleButton
                    onClick={startKcbFlow}
                    disabled={loading}
                >
                    {loading ? "본인인증 진행 중..." : "휴대폰 번호 본인인증"}
                </SingleButton>
            </div>
        </div>
    );
};

export default AdsRegistStep3;
